<script setup lang="ts">
/**
 * UiModalFooter — унифицированный подвал модалок (inbox-471).
 *
 * Сейчас 21+ модалок копируют один и тот же markup в `<template #footer>`:
 *   <UiButton variant="ghost" @click="cancel">Отмена</UiButton>
 *   <UiButton variant="primary" type="submit" :loading="...">Сохранить</UiButton>
 * Эта обёртка единственный источник правды:
 *   - Cancel = `variant="ghost"` h=40
 *   - Save = `variant="primary"|"danger"` h=40
 *   - Divider + 24px вертикальный отступ обеспечивает UiModal `.modal-foot`
 *     (border-top + padding 14px 24px) — не дублируем здесь.
 *
 * Usage (default):
 *   <template #footer>
 *     <UiModalFooter @cancel="close" @save="submit" />
 *   </template>
 *
 * Usage (form submit + custom labels):
 *   <template #footer>
 *     <UiModalFooter
 *       save-type="submit"
 *       save-form="create-bio-form"
 *       :save-disabled="!canSubmit"
 *       :save-loading="submitting"
 *       :cancel-disabled="submitting"
 *       @cancel="cancel"
 *     >
 *       <template #save>Создать и открыть конструктор</template>
 *     </UiModalFooter>
 *   </template>
 *
 * Usage (destructive action):
 *   <UiModalFooter save-variant="danger" @save="confirmDelete" @cancel="close">
 *     <template #save>Удалить</template>
 *   </UiModalFooter>
 */

import UiButton from '@/components/ui/UiButton.vue';

interface Props {
  /** Save button variant. Default 'primary'; 'danger' для destructive actions. */
  saveVariant?: 'primary' | 'danger';
  /** Save button type. 'submit' + saveForm для submission из формы вне footer'а. */
  saveType?: 'button' | 'submit';
  /** Form id для type='submit' (когда форма не оборачивает footer). */
  saveForm?: string;
  saveDisabled?: boolean;
  saveLoading?: boolean;
  cancelDisabled?: boolean;
  /** Не рендерить cancel-кнопку (для read-only confirm-only диалогов). */
  hideCancel?: boolean;
}

withDefaults(defineProps<Props>(), {
  saveVariant: 'primary',
  saveType: 'button',
  saveForm: undefined,
  saveDisabled: false,
  saveLoading: false,
  cancelDisabled: false,
  hideCancel: false,
});

defineEmits<{ cancel: []; save: [] }>();
</script>

<template>
  <UiButton
    v-if="!hideCancel"
    type="button"
    variant="ghost"
    size="md"
    :disabled="cancelDisabled"
    @click="$emit('cancel')"
  >
    <slot name="cancel">Отмена</slot>
  </UiButton>
  <UiButton
    :type="saveType"
    :form="saveForm"
    :variant="saveVariant"
    size="md"
    :disabled="saveDisabled"
    :loading="saveLoading"
    @click="$emit('save')"
  >
    <slot name="save">Сохранить</slot>
  </UiButton>
</template>
